import ReactPlayer from 'react-player';
import React, { ChangeEvent, useState, useEffect } from 'react';
import { ContentElementMediaDTO } from 'dto/contentElementMedia';
import './Video.css';
import { ProcessingStatus } from 'dto/file';
import { useStores } from 'util/mobx/stores';
import { observer } from 'mobx-react';
import ThumbnailMediaProgressTag from 'components/ThumbnailList/ThumbnailMediaProgressTag';
import { useTranslation } from 'react-i18next';
import { useSecureURL } from 'util/hooks';
import { xhrWithAuth } from 'api/common';
import Picture from '../Picture';

interface Props {
  file: ContentElementMediaDTO;
  small?: boolean;
  uploadProgress?: number;
  onMediaLabelChange?: (mediaId: string, text: string) => void;
  onDeleteImage?: (imageId: string) => void;
  viewOnly?: boolean;
}

const videoThumbnail = 'images/video_thumbnail.jpg';
const playIcon = '/images/icon_play_circle_filled_24px.svg';

const Video = observer(({ file, small, uploadProgress, onMediaLabelChange, onDeleteImage, viewOnly = false }: Props) => {
  const { t } = useTranslation('itemFormFlyOut');
  const [currentFile, setCurrentFile] = useState(file);
  const { domainStore } = useStores();

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onMediaLabelChange) {
      onMediaLabelChange(file.fileId, event.target.value);
    }
  };

  useEffect(() => {
    if (currentFile.status === ProcessingStatus.Pending || currentFile.status === ProcessingStatus.Processing) {
      domainStore.addFileToQuery(file.fileId);
    }

    const current = domainStore.queriedFiles.get(file.fileId);
    if (current !== undefined) {
      setCurrentFile(current);
    }
  }, [domainStore, file, domainStore.queriedFiles, currentFile.status]);

  const currentThumbnailURL = useSecureURL(currentFile.thumbnailUrl);

  if (viewOnly) {
    return <Picture src={file.thumbnailUrl} />;
  }

  return (
    <>
      <div className={onMediaLabelChange || onDeleteImage ? 'image_post_wide' : ''}>
        {currentFile.status === ProcessingStatus.Finished ? (
          <ReactPlayer
            width={240}
            height={small ? 110 : 180}
            light={currentThumbnailURL}
            className="react-player"
            url={currentFile.fullUrl}
            playIcon={<img alt="play" src={playIcon} height="48" className="video_play_icon" />}
            controls
            playing
            config={{
              file: {
                hlsOptions: {
                  forceHLS: true,
                  debug: false,
                  xhrSetup: xhrWithAuth
                }
              }
            }}
          />
        ) : (
          <>
            <ThumbnailMediaProgressTag
              status={currentFile.status}
              uploadProgress={uploadProgress}
              contentType={currentFile.contentType}
              small={small}
            />
            <Picture src={videoThumbnail} />
          </>
        )}
      </div>

      {onMediaLabelChange && file.status !== ProcessingStatus.Uploading ? (
        <input
          type="text"
          className="txt_post_image in_flyout post-image-input"
          placeholder={t('uploadFiles.imageDescription')}
          onChange={onChange}
          value={file.title}
        />
      ) : (
        <></>
      )}
      {onDeleteImage && file.status !== ProcessingStatus.Uploading ? (
        <div className="post_image_info">
          <div className="btn_remove_media" onClick={() => onDeleteImage(file.fileId)}>
            <img src="images/icon_bin_16.svg" alt="" className="image_function" />
            <div>{t('uploadFiles.deleteVideo')}</div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </>
  );
});

export default Video;
